import React, { useState, useRef } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Panel } from '@appkit4/react-components';
import { Accordion, AccordionItem } from '@appkit4/react-components/accordion';
import MainStructure from '../components/layout/MainStructure/MainStructure';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import UploadSample from '../components/features/UploadForm/UploadForm';
import PanelSample from '../components/features/TitlePanel/TitlePanel';
import { panelConfig, accordionItems } from '../config/docSimilarityConfig';
import { config } from '../config/env.config';
import AccordionSample from '../components/common/Accordion/Accordion';
import { apiClient } from '../services/apiClient';
import { getSignedUrl, uploadFile, downloadFile } from '../utils/fileUpload';
import { parsePageRange } from '../utils/pageParser';
import { handleApiError } from '../utils/errorHandler';

// New batch processing interfaces
interface DocSimilarityBatchRequest {
  original_blob: {
    name: string;
    document_type: '.zip';
    config: {
      ocr: boolean;
      pages: number[] | null;
    };
  };
  modified_blob: {
    name: string;
    document_type: '.zip';
    config: {
      ocr: boolean;
      pages: number[] | null;
    };
  };
}

interface JobSubmissionResponse {
  job_id: string;
  status: 'STARTED';
}

interface JobStatusResponse {
  status: 'STARTED' | 'MATCHING' | 'COMPARING' | 'AGGREGATING' | 'FINALIZING' | 'COMPLETED' | 'FAILED';
  total_pairs: number;
  completed_pairs: number;
  failed_pairs: number;
  excel_blob_name: string | null;
  excel_download_url?: string | null;
  error_message?: string;
}

// Endpoint constants (upload/download URLs now handled by utilities)
const DOC_SIMILARITY_JOB_ENDPOINT = config.api.endpoints.docSimilarityJob;
const JOB_STATUS_ENDPOINT = config.api.endpoints.jobStatus;

// All utility functions moved to utils/ directory:
// - parsePageRange → utils/pageParser.ts
// - getSignedUrl, uploadFile, downloadFile → utils/fileUpload.ts
// - handleApiError → utils/errorHandler.ts

// New batch processing API functions
const submitBatchJob = async (params: DocSimilarityBatchRequest): Promise<JobSubmissionResponse> => {
  return await apiClient.post<JobSubmissionResponse>(
    DOC_SIMILARITY_JOB_ENDPOINT,
    params
  );
};

const pollJobStatus = async (jobId: string): Promise<JobStatusResponse> => {
  const response = await apiClient.get<JobStatusResponse>(JOB_STATUS_ENDPOINT, {
    params: { job_id: jobId }
  });

  return response;
};

// JobStatusPanel Component
const JobStatusPanel: React.FC<{
  status: JobStatusResponse;
  onDownload: () => void;
  onReset: () => void;
  isDownloading: boolean;
}> = ({ status, onDownload, onReset, isDownloading }) => {
  const getStatusInfo = () => {
    switch (status.status) {
      case 'MATCHING':
        return {
          text: 'Abbinamento documenti',
          icon: 'icon-scan-outline',
          color: '#3B82F6'
        };
      case 'COMPARING':
        return {
          text: 'Confronto in corso',
          icon: 'icon-document-multiple-outline',
          color: '#8B5CF6'
        };
      case 'AGGREGATING':
        return {
          text: 'Aggregazione risultati',
          icon: 'icon-layers-outline',
          color: '#F59E0B'
        };
      case 'FINALIZING':
        return {
          text: 'Generazione report Excel',
          icon: 'icon-document-export-outline',
          color: '#10B981'
        };
      case 'COMPLETED':
        return {
          text: 'Analisi completata con successo!',
          icon: 'icon-check-circle-outline',
          color: '#22C55E'
        };
      case 'FAILED':
        return {
          text: 'Analisi fallita',
          icon: 'icon-alert-circle-outline',
          color: '#EF4444'
        };
      default:
        return {
          text: 'Preparazione analisi',
          icon: 'icon-time-outline',
          color: '#6B7280'
        };
    }
  };

  const getProgressPercentage = () => {
    if (status.total_pairs === 0) return 0;
    return Math.round((status.completed_pairs / status.total_pairs) * 100);
  };

  const statusInfo = getStatusInfo();

  return (
    <Panel className="job-status-panel">
      {/* Header con titolo */}
      <div style={{
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #555'
      }}>
        <h3 style={{ margin: 0, color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>
          {statusInfo.text}
        </h3>
      </div>

      {/* Progress Bar */}
      {status.status !== 'MATCHING' && status.total_pairs > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{ color: '#9AA4BE', fontSize: '0.9rem' }}>
              Progresso
            </span>
            <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>
              {getProgressPercentage()}%
            </span>
          </div>
          <div className="progress-bar" style={{ height: '12px', borderRadius: '6px', backgroundColor: '#2a2a2a', overflow: 'hidden' }}>
            <div
              className="progress-fill"
              style={{
                width: `${getProgressPercentage()}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
                transition: 'width 0.3s ease',
                borderRadius: '6px'
              }}
            />
          </div>
          <div style={{
            marginTop: '12px',
            color: '#9AA4BE',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            {status.completed_pairs} di {status.total_pairs} coppie elaborate
            {status.failed_pairs > 0 && (
              <span style={{ color: '#EF4444', fontWeight: 600, marginLeft: '8px' }}>
                ({status.failed_pairs} fallite)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Matching state (indeterminate) */}
      {status.status === 'MATCHING' && (
        <div style={{ marginBottom: '30px' }}>
          <p style={{
            color: '#9AA4BE',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            Inizializzazione e abbinamento documenti in corso...
          </p>
        </div>
      )}

      {/* Cancel Button (during processing) */}
      {(status.status === 'MATCHING' || status.status === 'COMPARING' || status.status === 'AGGREGATING' || status.status === 'FINALIZING') && (
        <div style={{
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '1px solid #555'
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={onReset}
              style={{
                backgroundColor: '#EF4444',
                borderColor: '#EF4444',
                color: '#fff',
                minWidth: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              Annulla Job
            </Button>
          </div>
          <p style={{
            color: '#9AA4BE',
            fontSize: '0.85rem',
            margin: '12px auto 0',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            Attenzione: questa azione fermerà l'elaborazione in corso
          </p>
        </div>
      )}

      {/* Action Buttons (when COMPLETED) */}
      {status.status === 'COMPLETED' && status.excel_download_url && (
        <>
          <div style={{
            display: 'flex',
            gap: '15px',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid #555'
          }}>
            <Button
              kind="primary"
              onClick={onDownload}
              disabled={isDownloading}
              style={{
                flex: 1,
                backgroundColor: '#22C55E',
                borderColor: '#22C55E'
              }}
              icon="icon-download-outline"
            >
              {isDownloading ? 'Download in corso...' : 'Scarica Report Excel'}
            </Button>
            <Button
              onClick={onReset}
              icon="icon-refresh-outline"
              style={{
                backgroundColor: '#9AA4BE',
                borderColor: '#9AA4BE',
                color: '#fff',
                minWidth: '180px'
              }}
            >
              Nuovo Confronto
            </Button>
          </div>

        </>
      )}

      {/* Failed state */}
      {status.status === 'FAILED' && (
        <div style={{
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '1px solid #555'
        }}>
          <p style={{
            color: '#EF4444',
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: '1.5'
          }}>
            {status.error_message ? (
              <>
                {status.error_message.includes('TOO_MANY_PAIRS') ? (
                  <>
                    <strong>Troppe coppie di file</strong><br />
                    {status.error_message.match(/Found (\d+) matching pairs/)?.[1] &&
                      `Trovate ${status.error_message.match(/Found (\d+) matching pairs/)?.[1]} coppie, ma il massimo consentito è 50.`
                    }<br />
                    Riduci il numero di file PDF nei tuoi archivi ZIP.
                  </>
                ) : (
                  status.error_message
                )}
              </>
            ) : (
              'Si è verificato un errore durante l\'elaborazione'
            )}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button kind="primary" onClick={onReset} icon="icon-refresh-outline">
              Riprova
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
};

const DocSimilarityPage: React.FC = () => {
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [modifiedFile, setModifiedFile] = useState<File | null>(null);
  const [originalPages, setOriginalPages] = useState('');
  const [modifiedPages, setModifiedPages] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Batch processing state
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [uploadAccordionKeys, setUploadAccordionKeys] = useState<string[]>(['upload-section']);
  const [resultsAccordionKeys, setResultsAccordionKeys] = useState<string[]>(['results-section']);
  const [uploadResetKey, setUploadResetKey] = useState(0);
  const { showSuccess, showError, showWarning } = useToast();

  // Download report function
  const downloadExcelReport = async (downloadUrl: string) => {
    setIsDownloading(true);

    try {
      await downloadFile(downloadUrl, 'similarity_report.xlsx');
      // Success feedback via browser download notification
    } catch (error) {
      console.error('Download error');
      const errorInfo = handleApiError(error);
      showError(errorInfo.message, errorInfo.title);
    } finally {
      setIsDownloading(false);
    }
  };


  // Polling effect — uses recursive setTimeout so requests never overlap.
  // pollingTimeoutRef is a ref (not state) so updating it never re-triggers this effect.
  React.useEffect(() => {
    if (!jobId || !isPolling) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await pollJobStatus(jobId);
        if (cancelled) return;

        setJobStatus(status);

        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          setIsPolling(false);
          if (status.status === 'COMPLETED') {
            setUploadAccordionKeys([]);
            setResultsAccordionKeys(['results-section']);
          }
        } else {
          // Schedule next poll only after this one completes
          pollingTimeoutRef.current = setTimeout(poll, 4000);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Polling error');
        // Retry after delay on transient errors
        pollingTimeoutRef.current = setTimeout(poll, 4000);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [jobId, isPolling]);

  const handleUploadFiles = async () => {
    if (!originalFile || !modifiedFile) {
      showError('Seleziona entrambi i file ZIP', 'File Mancanti');
      return;
    }

    setIsUploading(true);

    try {
      // Parse page ranges
      let originalPageArray: number[] | null = null;
      let modifiedPageArray: number[] | null = null;

      try {
        originalPageArray = parsePageRange(originalPages);
        modifiedPageArray = parsePageRange(modifiedPages);
      } catch (parseError: any) {
        showError(parseError.message);
        setIsUploading(false);
        return;
      }

      const safeOriginalName = originalFile.name.split(/[\\/]/).pop() || originalFile.name;
      const safeModifiedName = modifiedFile.name.split(/[\\/]/).pop() || modifiedFile.name;

      // Get signed URLs for both files
      const [originalSignedUrl, modifiedSignedUrl] = await Promise.all([
        getSignedUrl(safeOriginalName, 'application/zip'),
        getSignedUrl(safeModifiedName, 'application/zip')
      ]);

      // Upload ZIP files to GCS
      await Promise.all([
        uploadFile(originalSignedUrl, originalFile, 'application/zip'),
        uploadFile(modifiedSignedUrl, modifiedFile, 'application/zip')
      ]);

      // Submit batch job
      const jobRequest: DocSimilarityBatchRequest = {
        original_blob: {
          name: `vanilla/${safeOriginalName}`,
          document_type: '.zip',
          config: {
            ocr: false,  // Documento Digitale
            pages: originalPageArray
          }
        },
        modified_blob: {
          name: `vanilla/${safeModifiedName}`,
          document_type: '.zip',
          config: {
            ocr: true,   // Documento Scannerizzato
            pages: modifiedPageArray
          }
        }
      };

      const jobResponse = await submitBatchJob(jobRequest);
      setJobId(jobResponse.job_id);
      setIsPolling(true);

      // Chiudi upload, apri stato del job
      setUploadAccordionKeys([]);
      setResultsAccordionKeys(['results-section']);

      showSuccess('Analisi avviata!', '', { duration: 3000 });

      // Scroll verso il pannello dello stato dopo un breve delay
      setTimeout(() => {
        const resultsSection = document.querySelector('[data-section="results"]');
        if (resultsSection) {
          resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

    } catch (error) {
      console.error('Error during upload');

      // Use the error handler to get user-friendly messages
      const errorInfo = handleApiError(error);

      // Show error toast with appropriate styling based on error type
      const status = axios.isAxiosError(error) ? error.response?.status : null;

      // Use warning for specific user-actionable errors, error for system issues
      if (status === 400 || status === 422 || status === 404) {
        showWarning(errorInfo.message, errorInfo.title, {
          duration: 8000,
          clickToClose: true
        });
      } else if (status === 401 || status === 403 || status === 498) {
        showError(errorInfo.message, errorInfo.title, {
          duration: 10000,
          clickToClose: true
        });
      } else if (status === 503) {
        showWarning(errorInfo.message, errorInfo.title, {
          duration: 8000,
          clickToClose: true
        });
      } else {
        // 500 and other errors
        showError(errorInfo.message, errorInfo.title, {
          duration: 8000,
          clickToClose: true
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleOriginalFileChange = (newFile: File | null) => {
    setOriginalFile(newFile);
  };

  const handleModifiedFileChange = (newFile: File | null) => {
    setModifiedFile(newFile);
  };

  const handleReset = () => {
    // Stop polling if active
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Reset all state
    setOriginalFile(null);
    setModifiedFile(null);
    setOriginalPages('');
    setModifiedPages('');
    setJobId(null);
    setJobStatus(null);
    setIsPolling(false);
    setIsUploading(false);
    setIsDownloading(false);

    // Reopen upload accordion
    setUploadAccordionKeys(['upload-section']);
    setResultsAccordionKeys([]);

    // Reset upload components by changing key
    setUploadResetKey(prev => prev + 1);
  };

  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <MainStructure>
      <div className="docsimilarity-container">
          <PanelSample config={panelConfig}>
            <div>
              <p>
              <strong>DocSimilarity</strong> è uno strumento che analizza due documenti <strong>PDF</strong> e calcola la percentuale di somiglianza tra di essi.
                Lo strumento fornisce metriche dettagliate su testo, numeri e somiglianza totale.
              </p>
              <ul>
                <li>Il servizio è in versione <strong>Beta Test.</strong></li>
                <li>I tempi di elaborazione variano in base alla dimensione e complessità dei file.</li>
                <li>
                  Per feedback contattare: <a href="mailto:it_dig_customsolutions@pwc.com" className="email-link">it_dig_customsolutions@pwc.com</a>
                </li>
                <li>
                  <a href="https://pwcita.sharepoint.com/sites/IT-ADV-IT_AST_DIG_PA_COE/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FIT%2DADV%2DIT%5FAST%5FDIG%5FPA%5FCOE%2FShared%20Documents%2FSviluppi%2F6%2E%20DIG%2FDocument%20Operational%20Toolbox%20%2D%20DOT&viewid=853f9744%2De714%2D4d52%2Db72e%2D8ac987ec1845&p=true&ct=1772100110668&or=OWA%2DNT%2DMail" target="_blank" rel="noopener noreferrer" className="email-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span className="Appkit4-icon icon-document-text-outline"></span>
                    Analisi funzionale
                  </a>
                </li>
              </ul>
            </div>
          </PanelSample>

          <AccordionSample items={accordionItems} />

          <Accordion
            multiple={false}
            onClick={(keys: string[]) => setUploadAccordionKeys(keys)}
            activeKeys={uploadAccordionKeys}
          >
            <AccordionItem
              title="Upload Documenti"
              itemKey="upload-section"
            >
              <Panel
                className="ap-panel"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  filter: isHovered ? `brightness(90%)` : 'brightness(100%)',
                  padding: '20px',
                  borderRadius: '10px',
                  flex: 1,
                  position: 'relative',
                  opacity: (jobStatus && jobStatus.status !== 'COMPLETED' && jobStatus.status !== 'FAILED') ? 0.6 : 1,
                  pointerEvents: (jobStatus && jobStatus.status !== 'COMPLETED' && jobStatus.status !== 'FAILED') ? 'none' : 'auto'
                }}
              >
                {/* Overlay durante l'analisi */}
                {jobStatus && jobStatus.status !== 'COMPLETED' && jobStatus.status !== 'FAILED' && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: '10px',
                    zIndex: 10
                  }}>
                    <div style={{
                      backgroundColor: '#474747',
                      padding: '20px 30px',
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: '1px solid #D04A02'
                    }}>
                      <span className="Appkit4-icon icon-lock-outline" style={{
                        fontSize: '2rem',
                        color: '#D04A02',
                        display: 'block',
                        marginBottom: '10px'
                      }} />
                      <p style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>
                        Analisi in corso
                      </p>
                      <p style={{ color: '#9AA4BE', margin: '5px 0 0 0', fontSize: '0.85rem' }}>
                        Upload bloccato durante l'elaborazione
                      </p>
                    </div>
                  </div>
                )}
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUploadFiles();
                }}>
                  <div className="uploadersContainer">
                    <div className="uploaderWrapper">
                      <h3>Documento Digitale (ZIP)</h3>
                      <UploadSample
                        key={`original-${uploadResetKey}`}
                        onFileChange={handleOriginalFileChange}
                        acceptFileType='.zip'
                        
                      />
                      <div className="optionsGroup">
                        <div className="pageRangeInput">
                          <label htmlFor="originalPages">Pagine (opzionale)</label>
                          <input
                            type="text"
                            id="originalPages"
                            placeholder="es. 1-5, 1,3,5, o lascia vuoto per tutte"
                            value={originalPages}
                            onChange={(e) => setOriginalPages(e.target.value)}
                          />
                          <span className="helperText">
                            Formati: '1-5' per range, '1,3,5' per pagine specifiche, '1-3,5,7-9' per misto
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="uploaderWrapper">
                      <h3>Documento Scannerizzato (ZIP)</h3>
                      <UploadSample
                        key={`modified-${uploadResetKey}`}
                        onFileChange={handleModifiedFileChange}
                        acceptFileType='.zip'
                        
                      />
                      <div className="optionsGroup">
                        <div className="pageRangeInput">
                          <label htmlFor="modifiedPages">Pagine (opzionale)</label>
                          <input
                            type="text"
                            id="modifiedPages"
                            placeholder="es. 1-5, 1,3,5, o lascia vuoto per tutte"
                            value={modifiedPages}
                            onChange={(e) => setModifiedPages(e.target.value)}
                          />
                          <span className="helperText">
                            Formati: '1-5' per range, '1,3,5' per pagine specifiche, '1-3,5,7-9' per misto
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="button-container" style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      kind="primary"
                      onClick={handleUploadFiles}
                      disabled={isUploading || !originalFile || !modifiedFile}
                      loading={isUploading}
                      style={{ width: '250px', marginTop: '20px' }}
                      icon="icon-time-outline"
                    >
                      Controlla Similarity
                    </Button>
                  </div>
                </form>
              </Panel>
            </AccordionItem>
          </Accordion>

          {jobStatus && (
            <div data-section="results">
              <Accordion
                multiple={false}
                onClick={(keys: string[]) => setResultsAccordionKeys(keys)}
                activeKeys={resultsAccordionKeys}
              >
                <AccordionItem
                  title="Stato del Job"
                  itemKey="results-section"
                >
                  <JobStatusPanel
                    status={jobStatus}
                    onDownload={() => downloadExcelReport(jobStatus.excel_download_url!)}
                    onReset={handleReset}
                    isDownloading={isDownloading}
                  />
                </AccordionItem>
              </Accordion>
            </div>
          )}
      </div>
    </MainStructure>
  );
};

export default DocSimilarityPage;
